import { createFileRoute } from "@tanstack/react-router";
import { cardSurfaceClass } from "@/components/ui/card.tsx";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";

import { Response } from "@/components/ai-elements/response.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { m } from "@/paraglide/messages.js";
import {
	releaseContentQueryOptions,
	useReleasesInfiniteQuery,
} from "@/services/releases-service.ts";
import { cn } from "@/utils/cn.ts";
import { pageTitle } from "@/utils/page-title.ts";

export const Route = createFileRoute("/_authenticated/whats-new")({
	component: ReleasesPage,
	head: () => ({
		meta: [{ title: pageTitle(m.navigation_whats_new()) }],
	}),
});

interface ReleaseItem {
	version: string;
	date: string;
	breaking: boolean;
	path: string;
}

function ReleaseContentCard({ release }: { release: ReleaseItem }) {
	const { data } = useSuspenseQuery(
		releaseContentQueryOptions(release.version),
	);

	return (
		<>
			{data.fallback && (
				<p className="text-xs text-muted-foreground mb-3 italic">
					{m.whats_new_fallback_language()}
				</p>
			)}
			<Response className="text-sm">{data.content}</Response>
		</>
	);
}

function ReleaseContentSkeleton() {
	return (
		<div className="space-y-2 py-2">
			<Skeleton className="h-4 w-3/4" />
			<Skeleton className="h-4 w-full" />
			<Skeleton className="h-4 w-2/3" />
			<Skeleton className="h-4 w-5/6" />
		</div>
	);
}

function ReleasesPage() {
	const {
		data,
		isLoading,
		isError,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useReleasesInfiniteQuery();

	const [activeVersion, setActiveVersion] = useState<string | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const releases = useMemo(
		() =>
			data?.pages
				.flatMap((page) => {
					const typed = page as { releases?: ReleaseItem[] };
					return typed.releases ?? [];
				})
				.sort(
					(a, b) =>
						b.date.localeCompare(a.date) || b.version.localeCompare(a.version),
				) ?? [],
		[data],
	);

	const firstReleaseVersion = releases[0]?.version ?? null;

	const groupedByYear = useMemo(() => {
		const groups = new Map<string, ReleaseItem[]>();
		for (const release of releases) {
			const year = release.date.split("-")[0] ?? "unknown";
			const bucket = groups.get(year) ?? [];
			bucket.push(release);
			groups.set(year, bucket);
		}
		return Array.from(groups.entries()).sort(([a], [b]) => b.localeCompare(a));
	}, [releases]);

	useEffect(() => {
		if (!firstReleaseVersion || activeVersion) {
			return;
		}
		setActiveVersion(firstReleaseVersion);
	}, [firstReleaseVersion, activeVersion]);

	useEffect(() => {
		const sections = document.querySelectorAll<HTMLElement>(
			"[data-release-version]",
		);
		if (!sections.length) {
			return;
		}

		observerRef.current?.disconnect();

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort(
						(a, b) =>
							(a.target as HTMLElement).offsetTop -
							(b.target as HTMLElement).offsetTop,
					);
				if (visible[0]) {
					const version = visible[0].target.getAttribute(
						"data-release-version",
					);
					if (version) {
						setActiveVersion(version);
					}
				}
			},
			{ root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 },
		);

		for (const section of sections) {
			observer.observe(section);
		}
		observerRef.current = observer;

		return () => observer.disconnect();
	}, []);

	const scrollToVersion = (version: string) => {
		document
			.querySelector<HTMLElement>(`[data-release-version="${version}"]`)
			?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	if (isLoading) {
		return (
			<div className="flex flex-1 gap-8">
				<div className="w-56 hidden lg:flex flex-col gap-3 shrink-0">
					<Skeleton className="h-5 w-24" />
					<Skeleton className="h-7 w-full" />
					<Skeleton className="h-7 w-full" />
					<Skeleton className="h-7 w-3/4" />
				</div>
				<div className="flex-1 flex flex-col gap-6">
					<div className="space-y-2">
						<Skeleton className="h-8 w-48" />
						<Skeleton className="h-4 w-80" />
					</div>
					<Skeleton className="h-48 w-full" />
					<Skeleton className="h-48 w-full" />
				</div>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex flex-1 items-center justify-center">
				<div className="text-center space-y-2">
					<p className="text-sm text-muted-foreground">{m.whats_new_error()}</p>
					<p className="text-xs text-muted-foreground">
						{m.whats_new_error_retry()}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-1 gap-8 min-h-0">
			<aside className="hidden lg:flex w-56 shrink-0 flex-col gap-4 border-r pr-4 sticky top-0 self-start max-h-[calc(100vh-6rem)]">
				<div>
					<h2 className="text-sm font-semibold tracking-tight">
						{m.navigation_whats_new()}
					</h2>
					<p className="text-xs text-muted-foreground mt-0.5">
						{m.whats_new_subtitle()}
					</p>
				</div>
				<ScrollArea className="flex-1">
					<div className="flex flex-col gap-4 pr-2">
						{groupedByYear.map(([year, yearReleases]) => (
							<div key={year} className="space-y-0.5">
								<div className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">
									{year}
								</div>
								{yearReleases.map((release) => {
									const isActive = release.version === activeVersion;
									return (
										<button
											key={release.version}
											type="button"
											onClick={() => scrollToVersion(release.version)}
											className={cn(
												"flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs transition-colors",
												isActive
													? "bg-primary/10 text-primary font-medium"
													: "text-muted-foreground hover:bg-muted hover:text-foreground",
											)}
										>
											<span className="truncate font-mono text-[11px]">
												{release.version}
											</span>
											{release.breaking && (
												<span className="ml-1.5 size-1.5 rounded-full bg-destructive shrink-0" />
											)}
										</button>
									);
								})}
							</div>
						))}
					</div>
				</ScrollArea>
			</aside>

			<main className="flex-1 flex flex-col gap-6 min-w-0">
				<header>
					<h1 className="text-2xl font-semibold tracking-tight">
						{m.navigation_whats_new()}
					</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{m.whats_new_description()}
					</p>
				</header>

				{releases.map((release) => (
					<article
						key={release.version}
						data-release-version={release.version}
						className={cn(cardSurfaceClass, "scroll-mt-8 rounded-xl px-6 py-5")}
					>
						<header className="flex flex-wrap items-center gap-2 justify-between mb-4">
							<div className="flex items-center gap-2">
								<h2 className="font-mono text-base font-semibold">
									{release.version}
								</h2>
								{release.breaking && (
									<Badge variant="destructive" className="text-2xs px-1.5">
										{m.whats_new_breaking()}
									</Badge>
								)}
							</div>
							<time
								className="text-xs text-muted-foreground"
								dateTime={release.date}
							>
								{release.date}
							</time>
						</header>
						<Suspense fallback={<ReleaseContentSkeleton />}>
							<ReleaseContentCard release={release} />
						</Suspense>
					</article>
				))}

				{hasNextPage && (
					<div className="flex justify-center py-4">
						<Button
							variant="outline"
							size="sm"
							onClick={() => void fetchNextPage()}
							disabled={isFetchingNextPage}
						>
							{isFetchingNextPage ? "Loading…" : "Load more"}
						</Button>
					</div>
				)}
			</main>
		</div>
	);
}
